// Updated: accept .webp/.png/.jpg uploads up to 20MB, auto-convert to WebP at 80% quality; active upload feedback via spinner label and optimistic preview
import React, { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { Loader2 } from 'lucide-react';
import Collapsible from '../components/Collapsible';
import { useToastContext } from '../../../contexts/ToastContext';
import { validateImageFile, convertToWebp } from '../../../utils/imageUpload';

const HeroSection = () => {
  const [backgroundImage, setBackgroundImage] = useState('');
  const [previewImage, setPreviewImage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const { addToast } = useToastContext();

  useEffect(() => {
    fetchBackgroundImage();
  }, []);

  const fetchBackgroundImage = async () => {
    try {
      const { data, error } = await supabase
        .from('settings')
        .select('value')
        .eq('key', 'hero_background')
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      setBackgroundImage(data?.value || '');
    } catch (error) {
      console.error('Error fetching background image:', error);
    } finally {
      setLoading(false);
    }
  };

  const uploadBackgroundImage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) {
      return;
    }

    const rawFile = event.target.files[0];
    const validation = validateImageFile(rawFile);
    if (!validation.ok) {
      addToast(validation.message, 'error');
      event.target.value = '';
      return;
    }

    const objectUrl = URL.createObjectURL(rawFile);
    setPreviewImage(objectUrl);
    setUploading(true);

    try {
      const file = await convertToWebp(rawFile);

      // Delete old image if exists
      if (backgroundImage) {
        const oldFilePath = backgroundImage.split('/').pop();
        if (oldFilePath) {
          await supabase.storage
            .from('images')
            .remove([`hero/${oldFilePath}`]);
        }
      }

      const fileName = `background-${Date.now()}.webp`;
      const filePath = `hero/${fileName}`;

      // Upload new image
      const { error: uploadError } = await supabase.storage
        .from('images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(filePath);

      // Update settings
      const { error: updateError } = await supabase
        .from('settings')
        .upsert({ 
          key: 'hero_background',
          value: publicUrl
        }, {
          onConflict: 'key'
        });

      if (updateError) throw updateError;

      setBackgroundImage(publicUrl);
      addToast('Achtergrond succesvol bijgewerkt', 'success');
    } catch (error) {
      console.error('Error uploading background:', error);
      addToast('Er is een fout opgetreden bij het uploaden van de achtergrond', 'error');
    } finally {
      URL.revokeObjectURL(objectUrl);
      setPreviewImage('');
      setUploading(false);
      event.target.value = '';
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="space-y-6">
          <Collapsible title="Achtergrond aanpassen">
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Achtergrond afbeelding (.webp, .png, .jpg - max 20MB)
                </label>
                <div className="flex gap-4">
                  <label className={`flex-1 inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white ${uploading ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'}`}>
                    <input
                      type="file"
                      accept="image/webp,image/png,image/jpeg"
                      onChange={uploadBackgroundImage}
                      disabled={uploading}
                      className="hidden"
                    />
                    {uploading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Bezig met uploaden...</>
                    ) : (
                      'Kies bestand'
                    )}
                  </label>
                </div>
              </div>
            </div>
            {(previewImage || backgroundImage) && (
              <div className="mt-4 relative">
                <img
                  src={previewImage || backgroundImage}
                  alt="Hero background"
                  className={`w-full h-48 object-cover rounded-lg transition-opacity ${uploading ? 'opacity-70' : ''}`}
                />
                {uploading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-lg">
                    <Loader2 className="w-8 h-8 text-white animate-spin" />
                  </div>
                )}
              </div>
            )}
          </Collapsible>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;